"use client";

import { AnimatePresence, motion, type Transition } from "motion/react";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import useMeasure from "react-use-measure";
import { useShallow } from "zustand/react/shallow";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { IS_PRODUCTION, SUPPORT_PATH, SUPPORT_URL } from "@/constants/urls";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { useCheckoutStore } from "@/lib/store/checkout";
import { LucideIcon } from "./lucide-icon";

interface SupportContext {
  reference: string | null;
  merchantId: string | null;
  method: string | null;
  amount: string | null;
  errorCode: string | null;
}

const widthTransition: Transition = {
  duration: 0.27,
  ease: [0.25, 1, 0.5, 1],
  type: "spring",
  bounce: 0.2,
};

const labelTransition: Transition = {
  duration: 0.2,
  ease: [0.25, 1, 0.5, 1],
};

function storageBlocked() {
  try {
    window.localStorage.getItem("pc");
    return false;
  } catch {
    return true;
  }
}

const SupportButton = ({ embedded = false }: { embedded?: boolean }) => {
  const t = useTranslations("common");
  const [open, setOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isDesktop = useMediaQuery("(min-width: 64rem)");
  const [expanded, setExpanded] = useState(false);
  const [measureRef, bounds] = useMeasure();

  const context = useCheckoutStore(
    useShallow(
      (s): SupportContext => ({
        reference: s.hash || null,
        merchantId: s.business ?? null,
        method: s.selectedPaymentMethod ?? null,
        amount: s.total ? String(s.total) : null,
        errorCode: s.lastRejection ?? null,
      }),
    ),
  );

  if (IS_PRODUCTION || !SUPPORT_URL) return null;

  const sendContext = () => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: "paycode:context", payload: context },
      SUPPORT_URL,
    );
  };

  const handleClick = () => {
    if (storageBlocked()) {
      window.open(`${SUPPORT_URL}${SUPPORT_PATH}`, "_blank", "noopener");
      return;
    }
    setOpen(true);
  };

  const panel = (
    <iframe
      ref={iframeRef}
      title={t("support")}
      src={`${SUPPORT_URL}${SUPPORT_PATH}`}
      onLoad={sendContext}
      allow="clipboard-write"
      className="min-h-0 w-full flex-1 border-0"
    />
  );

  return (
    <>
      {embedded || !isDesktop ? (
        <button
          type="button"
          onClick={handleClick}
          className="mx-auto mb-4 flex cursor-pointer items-center gap-2 text-green-600 text-sm hover:opacity-80"
        >
          <LucideIcon
            name="message-circle-question-mark"
            className="size-4 shrink-0"
          />
          {t("needHelp")}
        </button>
      ) : (
        <motion.button
          type="button"
          aria-label={t("support")}
          title={t("support")}
          onClick={handleClick}
          onHoverStart={() => setExpanded(true)}
          onHoverEnd={() => setExpanded(false)}
          onFocus={() => setExpanded(true)}
          onBlur={() => setExpanded(false)}
          animate={{ width: bounds.width > 0 ? bounds.width : "auto" }}
          transition={widthTransition}
          className="fixed right-4 bottom-4 z-50 box-content flex cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[#70D688] text-neutral-900 shadow-lg outline-none focus-visible:ring-[#70D688]/50 focus-visible:ring-[3px]"
        >
          <div
            ref={measureRef}
            className="relative flex h-10 items-center justify-center gap-x-1.5 px-2.5"
          >
            <LucideIcon
              name="message-circle-question-mark"
              className="size-5 shrink-0"
            />
            <AnimatePresence mode="popLayout" initial={false}>
              {expanded && (
                <motion.span
                  key="need-help"
                  className="whitespace-nowrap text-sm"
                  initial={{ opacity: 0, filter: "blur(4px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, filter: "blur(4px)" }}
                  transition={labelTransition}
                >
                  {t("needHelp")}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </motion.button>
      )}

      {isDesktop ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="flex h-[600px] max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
            <DialogTitle className="sr-only">{t("support")}</DialogTitle>
            {panel}
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent className="h-[85dvh]">
            <DrawerTitle className="sr-only">{t("support")}</DrawerTitle>
            {panel}
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
};

export default SupportButton;
